import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from '../../utils/auth';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';
import { format, isWithin } from 'date-fns';
import { formatStatus } from '../../utils/formatters';
import {
  LuPlus, LuTruck, LuPackage, LuClipboardList, LuFileText,
  LuClock, LuActivity, LuArrowRight, LuCalendar, LuCircleAlert,
  LuChevronDown, LuArrowDownToLine, LuArrowUpFromLine
} from 'react-icons/lu';
import axios from 'axios';
import { API_URL } from '../../config';

const AdminDashboard = () => {
  const { userData: user } = getAuth();
  const [stats, setStats] = useState({
    pendingInboundRequests: 0,
    pendingOutboundRequests: 0,
    pendingKitRequests: 0,
    unpaidInvoices: 0,
    upcomingRequests: [],
    workInProgress: [],
    recentRequests: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return {};
    }
    return {
      Authorization: `Bearer ${token}`
    };
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(`${API_URL}/admin/dashboard/enhanced-stats`, {
          headers: getAuthHeaders()
        });
        
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        const errorMessage = error.response?.data?.error || error.message;
        setError(`Error fetching dashboard data: ${errorMessage}`);
        
        // Set fallback stats
        setStats({
          pendingInboundRequests: 0,
          pendingOutboundRequests: 0,
          pendingKitRequests: 0,
          unpaidInvoices: 0,
          upcomingRequests: [],
          workInProgress: [],
          recentRequests: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showQuickActions && !event.target.closest('.relative')) {
        setShowQuickActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showQuickActions]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <>
        <ErrorAlert message={error} />
        <div className="mt-4">
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="mt-4">Unable to load dashboard data. Please try again later.</p>
        </div>
      </>
    );
  }

  return (
    <div className="py-6">
      <div className="mx-auto px-4 sm:px-6 md:px-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500">Welcome back, {user?.name?.split(' ')[0] || 'there'}</p>
          </div>
          <div className="relative inline-block text-left">
            <button
              type="button"
              className="inline-flex items-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={() => setShowQuickActions(!showQuickActions)}
            >
              <LuPlus className="mr-2 h-4 w-4" />
              Quick Actions
              <LuChevronDown className="ml-2 h-4 w-4" />
            </button>

            {showQuickActions && (
              <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                <div className="py-1">
                  <Link to="/admin/requests/new?type=incoming" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setShowQuickActions(false)}>
                    <LuArrowDownToLine className="mr-3 h-4 w-4 text-gray-400" /> Create ASN (Incoming)
                  </Link>
                  <Link to="/admin/requests/new?type=ship" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setShowQuickActions(false)}>
                    <LuArrowUpFromLine className="mr-3 h-4 w-4 text-gray-400" /> Create Shipment (Outbound)
                  </Link>
                  <Link to="/admin/requests/new?type=crossdock" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setShowQuickActions(false)}>
                    <LuTruck className="mr-3 h-4 w-4 text-gray-400" /> Create Cross-Dock
                  </Link>
                  <Link to="/admin/requests/new?type=kit_build" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setShowQuickActions(false)}>
                    <LuClipboardList className="mr-3 h-4 w-4 text-gray-400" /> Create Kit Build
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 md:px-8">
        <div className="py-4">

          {/* Current Status */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Current Status</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <Link to="/admin/requests?type=incoming&status=pending" className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuArrowDownToLine className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Pending Inbound</p>
                      <p className="text-2xl font-semibold text-gray-900">{stats.pendingInboundRequests}</p>
                    </div>
                  </div>
                </div>
              </Link>

              <Link to="/admin/requests?type=ship&status=pending" className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuArrowUpFromLine className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Pending Outbound</p>
                      <p className="text-2xl font-semibold text-gray-900">{stats.pendingOutboundRequests}</p>
                    </div>
                  </div>
                </div>
              </Link>

              <Link to="/admin/requests?type=kit_build&status=pending" className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuClipboardList className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Pending Kits</p>
                      <p className="text-2xl font-semibold text-gray-900">{stats.pendingKitRequests}</p>
                    </div>
                  </div>
                </div>
              </Link>

              <Link to="/admin/invoices?status=pending,sent,overdue" className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <LuFileText className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Unpaid Invoices</p>
                      <p className="text-2xl font-semibold text-gray-900">{stats.unpaidInvoices}</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Two Column Layout for Lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Upcoming Requests (Next 48 Hours) */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <LuCalendar className="mr-2 h-5 w-5" />
                  Upcoming Requests (48hrs)
                </h2>
                <Link to="/admin/requests?upcoming=true" className="text-sm text-blue-600 hover:text-blue-500">
                  View all
                </Link>
              </div>
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                {stats.upcomingRequests && stats.upcomingRequests.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {stats.upcomingRequests.slice(0, 5).map((request) => (
                      <li key={request.id} className="px-4 py-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                request.requestType === 'incoming' ? 'bg-blue-100 text-blue-800' :
                                request.requestType === 'inbound_manifest' ? 'bg-indigo-100 text-indigo-800' :
                                request.requestType === 'ship' ? 'bg-green-100 text-green-800' :
                                request.requestType === 'crossdock' ? 'bg-purple-100 text-purple-800' :
                                'bg-orange-100 text-orange-800'
                              }`}>
                                {request.requestType}
                              </span>
                            </div>
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-900">
                                {request.requestNumber}
                              </p>
                              <p className="text-sm text-gray-500">
                                {request.Company?.name || 'Unknown Customer'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-900">
                              {format(new Date(request.scheduledDate), 'MMM d, h:mm a')}
                            </p>
                            <Link 
                              to={`/admin/requests/${request.id}`}
                              className="text-sm text-blue-600 hover:text-blue-500"
                            >
                              View details
                            </Link>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-4 py-6 text-center text-gray-500">
                    <LuCalendar className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-2">No upcoming requests in the next 48 hours</p>
                  </div>
                )}
              </div>
            </div>

            {/* Work in Progress */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <LuClock className="mr-2 h-5 w-5" />
                  In Progress
                </h2>
                <Link to="/admin/requests?status=in_progress" className="text-sm text-blue-600 hover:text-blue-500">
                  View all
                </Link>
              </div>
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                {stats.workInProgress && stats.workInProgress.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {stats.workInProgress.slice(0, 5).map((request) => (
                      <li key={request.id} className="px-4 py-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                request.requestType === 'incoming' ? 'bg-blue-100 text-blue-800' :
                                request.requestType === 'inbound_manifest' ? 'bg-indigo-100 text-indigo-800' :
                                request.requestType === 'ship' ? 'bg-green-100 text-green-800' :
                                request.requestType === 'crossdock' ? 'bg-purple-100 text-purple-800' :
                                'bg-orange-100 text-orange-800'
                              }`}>
                                {request.requestType}
                              </span>
                            </div>
                            <div className="ml-4">
                              <p className="text-sm font-medium text-gray-900">
                                {request.requestNumber}
                              </p>
                              <p className="text-sm text-gray-500">
                                {request.Company?.name || 'Unknown Customer'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              In Progress
                            </span>
                            <Link 
                              to={`/admin/requests/${request.id}`}
                              className="block text-sm text-blue-600 hover:text-blue-500 mt-1"
                            >
                              View details
                            </Link>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-4 py-6 text-center text-gray-500">
                    <LuClock className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-2">No requests currently in progress</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Requests */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Recent Requests</h2>
              <Link to="/admin/requests" className="text-sm text-blue-600 hover:text-blue-500">
                View all requests
              </Link>
            </div>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              {stats.recentRequests && stats.recentRequests.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {stats.recentRequests.slice(0, 8).map((request) => (
                    <li key={request.id} className="px-4 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              request.requestType === 'incoming' ? 'bg-blue-100 text-blue-800' :
                              request.requestType === 'ship' ? 'bg-green-100 text-green-800' :
                              request.requestType === 'crossdock' ? 'bg-purple-100 text-purple-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {request.requestType}
                            </span>
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-900">
                              {request.requestNumber}
                            </p>
                            <p className="text-sm text-gray-500">
                              {request.Company?.name || 'Unknown Customer'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            request.status === 'completed' ? 'bg-green-100 text-green-800' :
                            request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            request.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {formatStatus(request.status, request.requestType)}
                          </span>
                          <p className="text-sm text-gray-500 mt-1">
                            {format(new Date(request.createdAt), 'MMM d, yyyy')}
                          </p>
                          <Link 
                            to={`/admin/requests/${request.id}`}
                            className="text-sm text-blue-600 hover:text-blue-500"
                          >
                            View
                          </Link>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-6 text-center text-gray-500">
                  <LuClipboardList className="mx-auto h-12 w-12 text-gray-300" />
                  <p className="mt-2">No recent requests</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 